import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAdminMe, getReports } from '../api';
import AdminShell from './AdminShell';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

function formatCurrency(value, compact = false) {
  if (value === null || value === undefined || value === '') return '—';

  const num = Number(value);

  if (!Number.isFinite(num)) return '—';

  if (compact) {
    if (Math.abs(num) >= 10000000) {
      return `₹${(num / 10000000).toFixed(1)}Cr`;
    }

    if (Math.abs(num) >= 100000) {
      return `₹${(num / 100000).toFixed(1)}L`;
    }

    if (Math.abs(num) >= 1000) {
      return `₹${(num / 1000).toFixed(1)}K`;
    }
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(num);
}

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '—';

  const num = Number(value);

  if (!Number.isFinite(num)) return '—';

  return new Intl.NumberFormat('en-IN').format(num);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '—';

  const num = Number(value);

  if (!Number.isFinite(num)) return '—';

  return `${num > 0 ? '+' : ''}${num.toFixed(1)}%`;
}

function formatDate(dateString) {
  if (!dateString) return '—';

  const date = new Date(`${dateString}T00:00:00`);

  if (Number.isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function getInitials(name) {
  if (!name) return '—';

  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function getDefaultFromDate() {
  const date = new Date();
  date.setDate(date.getDate() - 29);
  return date.toISOString().slice(0, 10);
}

function getDefaultToDate() {
  return new Date().toISOString().slice(0, 10);
}

function getDateRangeLabel(from, to) {
  if (!from || !to) return 'Select date range';

  const fromDate = new Date(`${from}T00:00:00`);
  const toDate = new Date(`${to}T00:00:00`);

  if (
    Number.isNaN(fromDate.getTime()) ||
    Number.isNaN(toDate.getTime())
  ) {
    return `${from} - ${to}`;
  }

  const sameMonth =
    fromDate.getMonth() === toDate.getMonth() &&
    fromDate.getFullYear() === toDate.getFullYear();

  const fromLabel = fromDate.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short'
  });

  const toLabel = toDate.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  if (sameMonth) {
    return `${fromLabel} - ${toLabel}`;
  }

  return `${fromLabel} - ${toLabel}`;
}

function monthLabel(value) {
  if (!value) return '';

  const date = new Date(`${value}-01T00:00:00`);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-IN', {
    month: 'short'
  });
}

function monthFullLabel(value) {
  if (!value) return '';

  const date = new Date(`${value}-01T00:00:00`);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-IN', {
    month: 'short',
    year: 'numeric'
  });
}

function getChartPoints(values, width, height, padding) {
  if (!values.length) return [];

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  return values.map((value, index) => {
    const x =
      padding.left +
      (values.length === 1
        ? innerWidth / 2
        : (index / (values.length - 1)) * innerWidth);

    const y =
      padding.top +
      innerHeight -
      ((value - min) / range) * innerHeight;

    return {
      x,
      y
    };
  });
}

function pointsToPath(points) {
  if (!points.length) return '';

  return points
    .map((point, index) => {
      return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    })
    .join(' ');
}

function pointsToAreaPath(points, baseline) {
  if (!points.length) return '';

  const line = pointsToPath(points);
  const last = points[points.length - 1];
  const first = points[0];

  return `${line} L ${last.x} ${baseline} L ${first.x} ${baseline} Z`;
}

export default function ReportsManagement() {
  const [admin, setAdmin] = useState(null);

  const [reports, setReports] = useState(null);

  const [fromDate, setFromDate] = useState(getDefaultFromDate);
  const [toDate, setToDate] = useState(getDefaultToDate);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showFilters, setShowFilters] = useState(false);

  const [regionSearch, setRegionSearch] = useState('');
  const [categoryLimit, setCategoryLimit] = useState(6);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await getReports({
        from: fromDate,
        to: toDate
      });

      setReports(data.reports || null);
    } catch (err) {
      console.error('Failed to load reports:', err);
      setError(
        err.response?.data?.message ||
        'Failed to load reports and analytics.'
      );
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => {
        setAdmin(data.admin);
      })
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        window.location.href = '/login';
      });
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const overview = reports?.overview || {};

  const trend = useMemo(() => {
    return Array.isArray(reports?.revenueTrend)
      ? reports.revenueTrend
      : [];
  }, [reports]);

  const categories = useMemo(() => {
    const list = reports?.categoryDistribution?.categories || [];

    return [...list]
      .sort(
        (a, b) =>
          Number(b.activeVendors || 0) -
          Number(a.activeVendors || 0)
      );
  }, [reports]);

  const executives = useMemo(() => {
    return Array.isArray(reports?.executivePerformance)
      ? reports.executivePerformance
      : [];
  }, [reports]);

  const regions = useMemo(() => {
    const list = Array.isArray(reports?.regionalPerformance)
      ? reports.regionalPerformance
      : [];

    const query = regionSearch.trim().toLowerCase();

    if (!query) return list;

    return list.filter((item) => {
      return (
        item.region?.toLowerCase().includes(query) ||
        item.leadExecutive?.toLowerCase().includes(query)
      );
    });
  }, [reports, regionSearch]);

  const visibleCategories = categories.slice(0, categoryLimit);

  const totalCategories =
    reports?.categoryDistribution?.totalCategories ??
    categories.length;

  /*
   * ---------------------------------------------------------
   * Revenue chart
   * ---------------------------------------------------------
   */

  const chartWidth = 900;
  const chartHeight = 310;

  const chartPadding = {
    left: 58,
    right: 28,
    top: 30,
    bottom: 54
  };

  const revenueValues = trend.map((item) =>
    Number(item.revenue || 0)
  );

  const vendorValues = trend.map((item) =>
    Number(item.vendorsJoined || 0)
  );

  const revenuePoints = getChartPoints(
    revenueValues,
    chartWidth,
    chartHeight,
    chartPadding
  );

  const vendorPoints = getChartPoints(
    vendorValues,
    chartWidth,
    chartHeight,
    chartPadding
  );

  const chartBaseline =
    chartHeight - chartPadding.bottom;

  const maxRevenue = Math.max(...revenueValues, 0);

  const maxVendors = Math.max(...vendorValues, 0);

  const revenuePath = pointsToPath(revenuePoints);

  const revenueAreaPath = pointsToAreaPath(
    revenuePoints,
    chartBaseline
  );

  const vendorPath = pointsToPath(vendorPoints);

  /*
   * ---------------------------------------------------------
   * KPI values
   * ---------------------------------------------------------
   */

  const platformRevenue =
    overview.platformRevenue ?? null;

  const activeSubscriptions =
    overview.activeSubscriptions ?? null;

  const mrr = overview.mrr ?? null;

  const commissionsPaid =
    overview.commissionsPaid ?? null;

  const certificatesIssued =
    overview.certificatesIssued ?? null;

  const gstCertificates =
    overview.certificates?.gst ?? 0;

  const msmeCertificates =
    overview.certificates?.msme ?? 0;

  const labourCertificates =
    overview.certificates?.labour ?? 0;

  const commissionRate =
    overview.commissionRate ?? null;

  const vendorsJoined =
    overview.vendorsJoined ?? null;

  /*
   * The current backend doesn't yet provide these metrics.
   * Keep them as null instead of inventing values.
   */
  const revenueGrowth =
    overview.revenueGrowth ?? null;

  const renewalRate =
    overview.renewalRate ?? null;

  const displayedCommissionRate =
    commissionRate !== null
      ? `${commissionRate}%`
      : '—';

  const handleDateApply = (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) return;

    if (fromDate > toDate) {
      setError('The start date cannot be after the end date.');
      return;
    }

    loadReports();
  };

  const handlePreset = (days) => {
    const to = new Date();
    const from = new Date();

    from.setDate(from.getDate() - (days - 1));

    setFromDate(from.toISOString().slice(0, 10));
    setToDate(to.toISOString().slice(0, 10));
  };

  const handleExportCsv = () => {
    if (!reports) return;

    const rows = [];

    rows.push([
      'Region',
      'Lead Executive',
      'Active Vendors',
      'Subscriptions Sold',
      'Commission Value'
    ]);

    regions.forEach((row) => {
      rows.push([
        row.region || '',
        row.leadExecutive || '',
        row.activeVendors ?? 0,
        row.subscriptionsSold ?? 0,
        row.commissionValue ?? 0
      ]);
    });

    const csv = rows
      .map((row) =>
        row
          .map((value) => {
            const stringValue = String(value ?? '');

            return `"${stringValue.replace(/"/g, '""')}"`;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `milieu-reports-${fromDate}-to-${toDate}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <AdminShell
      admin={admin}
      badges={{}}
      pageTitle="Reports & Performance Analytics"
      searchValue={regionSearch}
      onSearchChange={setRegionSearch}
      searchPlaceholder="Search analytics, vendor logs, certificates..."
      headerExtra={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}
        >
          <div className="dash-search-wrapper">
            <span className="material-symbols-outlined dash-search-icon">
              search
            </span>

            <input
              className="dash-search-input"
              type="text"
              placeholder="Search analytics, vendor logs, certificates..."
              value={regionSearch}
              onChange={(e) => setRegionSearch(e.target.value)}
            />
          </div>

          <button
            className="dash-quick-btn"
            onClick={handleExportCsv}
            disabled={!reports || loading}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 18 }}
            >
              download
            </span>

            Export CSV
          </button>
        </div>
      }
    >
      <div className="glass-page">
        {/* =====================================================
            PAGE HEADER
        ====================================================== */}

        <div className="dash-welcome-row">
          <div>
            

            <h2 className="dash-welcome-subtitle">
              Comprehensive insights on vendor growth, platform
              revenue, executive performance, and certificate
              filings.
            </h2>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}
          >
            <button
              className="dash-filter-select"
              style={{
                cursor: 'pointer',
                minHeight: 38
              }}
              onClick={() =>
                setShowFilters((value) => !value)
              }
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 17,
                  verticalAlign: 'middle',
                  marginRight: 5
                }}
              >
                tune
              </span>

              Filters
            </button>

            <button
              className="dash-quick-btn primary"
              onClick={handleExportCsv}
              disabled={!reports || loading}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 18 }}
              >
                download
              </span>

              Download Full Report
            </button>
          </div>
        </div>

        {/* =====================================================
            DATE / FILTER PANEL
        ====================================================== */}

        {showFilters && (
          <section
            className="glass-panel"
            style={{
              marginBottom: 20,
              padding: 18
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 14,
                flexWrap: 'wrap'
              }}
            >
              <div className="dash-modal-field">
                <label htmlFor="reportsFrom">
                  From
                </label>

                <input
                  id="reportsFrom"
                  type="date"
                  className="admin-text-input"
                  value={fromDate}
                  onChange={(e) =>
                    setFromDate(e.target.value)
                  }
                />
              </div>

              <div className="dash-modal-field">
                <label htmlFor="reportsTo">
                  To
                </label>

                <input
                  id="reportsTo"
                  type="date"
                  className="admin-text-input"
                  value={toDate}
                  onChange={(e) =>
                    setToDate(e.target.value)
                  }
                />
              </div>

              <button
                className="dash-modal-btn primary"
                onClick={handleDateApply}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Apply'}
              </button>

              <button
                className="dash-modal-btn secondary"
                onClick={() => handlePreset(30)}
              >
                Last 30 Days
              </button>

              <button
                className="dash-modal-btn secondary"
                onClick={() => handlePreset(90)}
              >
                Last 90 Days
              </button>
            </div>
          </section>
        )}

        {/* =====================================================
            PERIOD INDICATOR
        ====================================================== */}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 18,
            flexWrap: 'wrap'
          }}
        >
          <div>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'rgba(40,45,52,0.55)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em'
              }}
            >
              Reporting period
            </span>

            <div
              style={{
                fontSize: 16,
                fontWeight: 750,
                color: '#12151b',
                marginTop: 3
              }}
            >
              {getDateRangeLabel(fromDate, toDate)}
            </div>
          </div>

          {reports?.period && (
            <span className="glass-pill-badge">
              Live database analytics
            </span>
          )}
        </div>

        {/* =====================================================
            ERROR
        ====================================================== */}

        {error && (
          <div
            className="admin-banner error"
            style={{ marginBottom: 20 }}
          >
            {error}
          </div>
        )}

        {/* =====================================================
            KPI GRID
        ====================================================== */}

        <div className="glass-kpi-grid">
          {/* Revenue */}

          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Total Platform Revenue</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                payments
              </span>
            </div>

            <div className="glass-kpi-value">
              {loading
                ? '…'
                : formatCurrency(platformRevenue, true)}
            </div>

            <div className="glass-kpi-sub">
              {revenueGrowth !== null ? (
                <>
                  <strong
                    style={{
                      color:
                        Number(revenueGrowth) >= 0
                          ? '#15803d'
                          : '#b91c1c'
                    }}
                  >
                    {formatPercent(revenueGrowth)}
                  </strong>{' '}
                  vs previous period
                </>
              ) : (
                'Subscription revenue in selected period'
              )}
            </div>
          </article>

          {/* Active subscriptions */}

          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Active Subscriptions</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                subscriptions
              </span>
            </div>

            <div className="glass-kpi-value">
              {loading
                ? '…'
                : formatNumber(activeSubscriptions)}
            </div>

            <div className="glass-kpi-sub">
              MRR:{' '}
              <strong>
                {loading
                  ? '…'
                  : formatCurrency(mrr, true)}
              </strong>
            </div>

            <div
              style={{
                marginTop: 5,
                fontSize: 11,
                color: 'rgba(40,45,52,0.45)'
              }}
            >
              Renewal rate:{' '}
              {renewalRate !== null
                ? `${Number(renewalRate).toFixed(1)}%`
                : '—'}
            </div>
          </article>

          {/* Commission */}

          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Commissions Paid</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                account_balance_wallet
              </span>
            </div>

            <div className="glass-kpi-value">
              {loading
                ? '…'
                : formatCurrency(
                    commissionsPaid,
                    true
                  )}
            </div>

            <div className="glass-kpi-sub">
              {commissionsPaid === null ? (
                <>
                  Payment ledger not available yet
                </>
              ) : (
                <>
                  Commission payout rate:{' '}
                  <strong>
                    {displayedCommissionRate}
                  </strong>
                </>
              )}
            </div>
          </article>

          {/* Certificates */}

          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Certificates Issued</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                verified
              </span>
            </div>

            <div className="glass-kpi-value">
              {loading
                ? '…'
                : formatNumber(certificatesIssued)}
            </div>

            <div className="glass-kpi-sub">
              GST {formatNumber(gstCertificates)}
              {' · '}
              MSME {formatNumber(msmeCertificates)}
              {' · '}
              Labour {formatNumber(labourCertificates)}
            </div>
          </article>
        </div>

        {/* =====================================================
            SECONDARY SUMMARY
        ====================================================== */}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            marginBottom: 24
          }}
        >
          <div
            className="glass-panel"
            style={{
              marginBottom: 0,
              padding: 16
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'rgba(40,45,52,0.55)',
                fontWeight: 700
              }}
            >
              VENDORS JOINED
            </div>

            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                marginTop: 4
              }}
            >
              {formatNumber(vendorsJoined)}
            </div>
          </div>

          <div
            className="glass-panel"
            style={{
              marginBottom: 0,
              padding: 16
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'rgba(40,45,52,0.55)',
                fontWeight: 700
              }}
            >
              ACTIVE CATEGORIES
            </div>

            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                marginTop: 4
              }}
            >
              {formatNumber(totalCategories)}
            </div>
          </div>

          <div
            className="glass-panel"
            style={{
              marginBottom: 0,
              padding: 16
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'rgba(40,45,52,0.55)',
                fontWeight: 700
              }}
            >
              COMMISSION MODEL
            </div>

            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                marginTop: 4
              }}
            >
              {displayedCommissionRate}
            </div>
          </div>
        </div>

        {/* =====================================================
            REVENUE / ONBOARDING CHART
        ====================================================== */}

        <section className="glass-panel">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
              marginBottom: 18,
              flexWrap: 'wrap'
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 750,
                  color: 'rgba(40,45,52,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em'
                }}
              >
                Growth trajectory
              </span>

              <h3
                className="dash-panel-title"
                style={{
                  fontSize: 20,
                  marginTop: 3
                }}
              >
                Monthly Revenue & Onboarding Trajectory
              </h3>

              <p
                style={{
                  margin: '5px 0 0',
                  fontSize: 12,
                  color: 'rgba(40,45,52,0.5)'
                }}
              >
                Subscription revenue and vendors joining
                the platform over time.
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                fontSize: 12,
                fontWeight: 650,
                color: 'rgba(40,45,52,0.6)'
              }}
            >
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: 'rgba(80,110,200,0.8)',
                    marginRight: 6
                  }}
                />
                Revenue
              </span>

              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: 'rgba(50,150,100,0.7)',
                    marginRight: 6
                  }}
                />
                Vendors
              </span>
            </div>
          </div>

          {loading ? (
            <div className="dash-empty-state">
              Loading analytics...
            </div>
          ) : trend.length === 0 ? (
            <div className="dash-empty-state">
              No trend data available for this period.
            </div>
          ) : (
            <div
              style={{
                width: '100%',
                overflowX: 'auto'
              }}
            >
              <svg
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                width="100%"
                height="310"
                role="img"
                aria-label="Monthly revenue and vendor onboarding chart"
                style={{
                  minWidth: 650,
                  display: 'block'
                }}
              >
                {/* Horizontal grid */}

                {[0, 1, 2, 3, 4].map((index) => {
                  const y =
                    chartPadding.top +
                    ((chartHeight -
                      chartPadding.top -
                      chartPadding.bottom) /
                      4) *
                      index;

                  return (
                    <line
                      key={index}
                      x1={chartPadding.left}
                      y1={y}
                      x2={
                        chartWidth -
                        chartPadding.right
                      }
                      y2={y}
                      stroke="rgba(30,35,42,0.08)"
                      strokeWidth="1"
                    />
                  );
                })}

                {/* Revenue area */}

                <path
                  d={revenueAreaPath}
                  fill="rgba(100,130,220,0.08)"
                  stroke="none"
                />

                {/* Revenue line */}

                <path
                  d={revenuePath}
                  fill="none"
                  stroke="rgba(80,110,200,0.85)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Vendor line */}

                <path
                  d={vendorPath}
                  fill="none"
                  stroke="rgba(50,150,100,0.7)"
                  strokeWidth="2"
                  strokeDasharray="5 5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Revenue points */}

                {revenuePoints.map((point, index) => (
                  <circle
                    key={`revenue-${index}`}
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill="white"
                    stroke="rgba(80,110,200,0.9)"
                    strokeWidth="2"
                  />
                ))}

                {/* Vendor points */}

                {vendorPoints.map((point, index) => (
                  <circle
                    key={`vendor-${index}`}
                    cx={point.x}
                    cy={point.y}
                    r="3"
                    fill="white"
                    stroke="rgba(50,150,100,0.8)"
                    strokeWidth="2"
                  />
                ))}

                {/* X-axis labels */}

                {trend.map((item, index) => {
                  const point =
                    revenuePoints[index];

                  return (
                    <text
                      key={`label-${item.month}`}
                      x={point?.x || 0}
                      y={chartHeight - 20}
                      textAnchor="middle"
                      fontSize="11"
                      fill="rgba(40,45,52,0.5)"
                    >
                      {monthLabel(item.month)}
                    </text>
                  );
                })}

                {/* Y-axis revenue labels */}

                {[0, 0.25, 0.5, 0.75, 1].map(
                  (ratio, index) => {
                    const value =
                      maxRevenue * ratio;

                    const y =
                      chartPadding.top +
                      (1 - ratio) *
                        (chartHeight -
                          chartPadding.top -
                          chartPadding.bottom);

                    return (
                      <text
                        key={`y-${index}`}
                        x="48"
                        y={y + 4}
                        textAnchor="end"
                        fontSize="10"
                        fill="rgba(40,45,52,0.45)"
                      >
                        {formatCurrency(
                          value,
                          true
                        )}
                      </text>
                    );
                  }
                )}

                {/* Vendor max indicator */}

                {maxVendors > 0 && (
                  <text
                    x={
                      chartWidth -
                      chartPadding.right
                    }
                    y={chartPadding.top - 8}
                    textAnchor="end"
                    fontSize="10"
                    fill="rgba(50,150,100,0.65)"
                  >
                    Max vendors: {formatNumber(maxVendors)}
                  </text>
                )}

                {/* Revenue max indicator */}

                {maxRevenue > 0 && (
                  <text
                    x={chartPadding.left}
                    y={chartPadding.top - 8}
                    fontSize="10"
                    fill="rgba(80,110,200,0.75)"
                  >
                    Peak: {formatCurrency(maxRevenue, true)}
                  </text>
                )}
              </svg>
            </div>
          )}

          {trend.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 8,
                marginTop: 8
              }}
            >
              {trend.map((item) => (
                <div
                  key={item.month}
                  style={{
                    padding: '9px 10px',
                    borderRadius: 12,
                    background:
                      'rgba(255,255,255,0.18)',
                    border:
                      '1px solid rgba(255,255,255,0.35)'
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color:
                        'rgba(40,45,52,0.45)',
                      fontWeight: 700
                    }}
                  >
                    {monthFullLabel(item.month)}
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 750,
                      marginTop: 3
                    }}
                  >
                    {formatCurrency(
                      item.revenue,
                      true
                    )}
                  </div>

                  <div
                    style={{
                      fontSize: 10,
                      color:
                        'rgba(40,45,52,0.45)',
                      marginTop: 2
                    }}
                  >
                    {formatNumber(
                      item.vendorsJoined
                    )}{' '}
                    vendors
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* =====================================================
            CATEGORY DISTRIBUTION
        ====================================================== */}

        <section className="glass-panel">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
              marginBottom: 20,
              flexWrap: 'wrap'
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 750,
                  color: 'rgba(40,45,52,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em'
                }}
              >
                Vendor mix
              </span>

              <h3
                className="dash-panel-title"
                style={{
                  fontSize: 20,
                  marginTop: 3
                }}
              >
                Category Distribution
              </h3>

              <p
                style={{
                  margin: '5px 0 0',
                  fontSize: 12,
                  color: 'rgba(40,45,52,0.5)'
                }}
              >
                Distribution of active vendors across
                service categories.
              </p>
            </div>

            <div
              style={{
                textAlign: 'right'
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(40,45,52,0.45)',
                  fontWeight: 700
                }}
              >
                TOTAL ACTIVE VENDORS
              </div>

              <div
                style={{
                  fontSize: 25,
                  fontWeight: 800,
                  color: '#12151b'
                }}
              >
                {formatNumber(
                  reports?.categoryDistribution
                    ?.totalActiveVendors ?? 0
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="dash-empty-state">
              Loading categories...
            </div>
          ) : visibleCategories.length === 0 ? (
            <div className="dash-empty-state">
              No category data available.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(250px, 1fr))',
                gap: 12
              }}
            >
              {visibleCategories.map(
                (category, index) => {
                  const percentage =
                    Number(category.percentage || 0);

                  return (
                    <div
                      key={
                        category.id ||
                        category.name ||
                        index
                      }
                      style={{
                        padding: 16,
                        borderRadius: 18,
                        border:
                          '1px solid rgba(255,255,255,0.45)',
                        background:
                          'rgba(255,255,255,0.13)',
                        backdropFilter:
                          'blur(18px)',
                        WebkitBackdropFilter:
                          'blur(18px)'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent:
                            'space-between',
                          alignItems: 'center',
                          gap: 10
                        }}
                      >
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 750,
                            color: '#171b21'
                          }}
                        >
                          {category.name}
                        </div>

                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 800
                          }}
                        >
                          {percentage.toFixed(1)}%
                        </div>
                      </div>

                      <div
                        style={{
                          height: 7,
                          borderRadius: 999,
                          background:
                            'rgba(30,35,42,0.08)',
                          overflow: 'hidden',
                          marginTop: 12
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min(
                              percentage,
                              100
                            )}%`,
                            height: '100%',
                            borderRadius: 999,
                            background:
                              'linear-gradient(90deg, rgba(100,130,220,0.55), rgba(150,120,255,0.55))'
                          }}
                        />
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent:
                            'space-between',
                          marginTop: 11,
                          fontSize: 11,
                          color:
                            'rgba(40,45,52,0.5)'
                        }}
                      >
                        <span>
                          Active vendors:{' '}
                          <strong
                            style={{
                              color: '#171b21'
                            }}
                          >
                            {formatNumber(
                              category.activeVendors
                            )}
                          </strong>
                        </span>

                        <span>
                          Revenue:{' '}
                          <strong
                            style={{
                              color: '#171b21'
                            }}
                          >
                            {category.revenue !==
                            undefined
                              ? formatCurrency(
                                  category.revenue,
                                  true
                                )
                              : '—'}
                          </strong>
                        </span>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}

          {categories.length > 6 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <button
                className="dash-link-btn"
                onClick={() =>
                  setCategoryLimit((current) =>
                    current >= categories.length
                      ? 6
                      : categories.length
                  )
                }
              >
                {categoryLimit >= categories.length
                  ? 'Show less'
                  : `View all ${categories.length} categories`}
              </button>
            </div>
          )}
        </section>

        {/* =====================================================
            REGIONAL / EXECUTIVE PERFORMANCE
        ====================================================== */}

        <section className="glass-panel glass-table-panel">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 18
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 750,
                  color: 'rgba(40,45,52,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em'
                }}
              >
                Performance
              </span>

              <h3
                className="dash-panel-title"
                style={{
                  fontSize: 20,
                  marginTop: 3
                }}
              >
                Top Performing Regions & Executives
              </h3>

              <p
                style={{
                  margin: '5px 0 0',
                  fontSize: 12,
                  color: 'rgba(40,45,52,0.5)'
                }}
              >
                Current vendor and subscription performance
                grouped by city and sales executive.
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center'
              }}
            >
              <button
                className="dash-quick-btn"
                onClick={handleExportCsv}
                disabled={!regions.length}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 17 }}
                >
                  file_download
                </span>
                CSV
              </button>
            </div>
          </div>

          {loading ? (
            <div className="dash-empty-state">
              Loading performance data...
            </div>
          ) : regions.length === 0 ? (
            <div className="dash-empty-state">
              No regional performance data available.
            </div>
          ) : (
            <>
              <div className="dash-directory-table-wrap">
                <table className="dash-directory-table">
                  <thead>
                    <tr>
                      <th>Region</th>
                      <th>Lead Executive</th>
                      <th>Active Vendors</th>
                      <th>Subscriptions Sold</th>
                      <th>Total Commission</th>
                      <th>Growth</th>
                      <th className="right">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {regions.slice(0, 25).map(
                      (row, index) => (
                        <tr
                          key={`${row.region}-${index}`}
                        >
                          <td>
                            <div className="glass-vendor-cell">
                              <div className="glass-avatar-tile">
                                <span className="material-symbols-outlined">
                                  location_city
                                </span>
                              </div>

                              <div>
                                <p className="dash-exec-name">
                                  {row.region ||
                                    'Unknown'}
                                </p>

                                <p className="dash-exec-email">
                                  Regional hub
                                </p>
                              </div>
                            </div>
                          </td>

                          <td>
                            <div className="glass-vendor-cell">
                              <div className="glass-avatar-tile small">
                                {getInitials(
                                  row.leadExecutive
                                )}
                              </div>

                              <span
                                style={{
                                  fontWeight: 650
                                }}
                              >
                                {row.leadExecutive ||
                                  'Unassigned'}
                              </span>
                            </div>
                          </td>

                          <td>
                            <strong>
                              {formatNumber(
                                row.activeVendors
                              )}
                            </strong>
                          </td>

                          <td>
                            <strong>
                              {formatNumber(
                                row.subscriptionsSold
                              )}
                            </strong>
                          </td>

                          <td>
                            {formatCurrency(
                              row.commissionValue
                            )}
                          </td>

                          <td>
                            {row.growth !==
                            undefined &&
                            row.growth !== null ? (
                              <span
                                style={{
                                  fontWeight: 700,
                                  color:
                                    Number(
                                      row.growth
                                    ) >= 0
                                      ? '#15803d'
                                      : '#b91c1c'
                                }}
                              >
                                {formatPercent(
                                  row.growth
                                )}
                              </span>
                            ) : (
                              <span
                                style={{
                                  color:
                                    'rgba(40,45,52,0.4)'
                                }}
                              >
                                —
                              </span>
                            )}
                          </td>

                          <td className="dash-row-actions">
                            <button
                              className="dash-link-btn"
                              title="View details"
                              onClick={() => {
                                setRegionSearch(
                                  row.region || ''
                                );
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems: 'center',
                  marginTop: 14,
                  fontSize: 12,
                  color: 'rgba(40,45,52,0.5)',
                  flexWrap: 'wrap',
                  gap: 10
                }}
              >
                <span>
                  Showing{' '}
                  <strong>
                    {Math.min(regions.length, 25)}
                  </strong>{' '}
                  of{' '}
                  <strong>{regions.length}</strong>{' '}
                  regions
                </span>

                {executives.length > 0 && (
                  <span>
                    {executives.length} sales executives
                    tracked
                  </span>
                )}
              </div>
            </>
          )}
        </section>

        {/* =====================================================
            DATA DEFINITIONS / HONEST METRICS
        ====================================================== */}

        <section
          className="glass-panel"
          style={{
            padding: 18
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start'
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: 20,
                color: 'rgba(40,45,52,0.45)'
              }}
            >
              info
            </span>

            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 750,
                  color: '#171b21'
                }}
              >
                Analytics data note
              </div>

              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 12,
                  lineHeight: 1.55,
                  color: 'rgba(40,45,52,0.55)'
                }}
              >
                Revenue and subscription analytics are based
                on the currently available subscription
                records. Commission payouts and historical
                renewal tracking require dedicated transaction
                history, so those values are shown as unavailable
                rather than estimated.
              </p>
            </div>
          </div>
        </section>
      </div>
    </AdminShell>
  );
}